<template>
  <section class="featured-categories">
    <h2 class="section-title">Featured Categories</h2>

    <div
      ref="viewport"
      class="marquee-viewport"
      :class="{ dragging: isDragging }"
      @mouseenter="hovering = true"
      @mouseleave="hovering = false"
      @scroll="wrap"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <div ref="track" class="marquee-track">
        <router-link
          v-for="(category, i) in loopCategories"
          :key="`featured-cat-${i}`"
          class="category-item"
          :to="{ path: '/category', query: { c: category.slug } }"
          @click="onItemClick"
        >
          <div
            class="category-frame"
            :style="{ background: gradients[i % gradients.length] }"
          >
            <i :class="category.icon" class="category-icon"></i>
          </div>
          <div class="category-label">{{ category.label }}</div>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "FeaturedCategories",
  data() {
    return {
      hovering: false,
      isDragging: false,
      pointerActive: false,
      dragMoved: false,
      startX: 0,
      startScroll: 0,
      setWidth: 0,
      rafId: null,
      speed: 0.6,
      gradients: [
        "linear-gradient(135deg, #f6a6ff 0%, #b3a0ff 100%)",
        "linear-gradient(135deg, #a0c4ff 0%, #bdb2ff 100%)",
        "linear-gradient(135deg, #c7e9ff 0%, #a3c4f3 100%)",
        "linear-gradient(135deg, #ffd6a5 0%, #ff9db0 100%)",
        "linear-gradient(135deg, #cde7ff 0%, #a9d6ff 100%)",
        "linear-gradient(135deg, #fff3a0 0%, #ffd36e 100%)",
        "linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)",
        "linear-gradient(135deg, #ffe29f 0%, #ffa99f 100%)",
      ],
      categories: [
        { label: "New Arrivals", slug: "new-arrivals", icon: "bi bi-stars" },
        { label: "J-Beauty", slug: "j-beauty", icon: "bi bi-flower1" },
        { label: "K-Beauty", slug: "k-beauty", icon: "bi bi-heart" },
        { label: "International Brands", slug: "international-brands", icon: "bi bi-globe" },
        { label: "Baby Care", slug: "baby-care", icon: "bi bi-emoji-smile" },
        { label: "Make Up", slug: "make-up", icon: "bi bi-brush" },
        { label: "Doctor Cosmetics", slug: "doctor-cosmetics", icon: "bi bi-heart-pulse" },
        { label: "Beauty Tips", slug: "beauty-tips", icon: "bi bi-lightbulb" },
        { label: "AI Suggestion", slug: "ai-suggestion", icon: "bi bi-robot" },
        { label: "Community", slug: "community", icon: "bi bi-people" },
        { label: "Appointment", slug: "appointment", icon: "bi bi-calendar-check" },
        { label: "Offer", slug: "offer", icon: "bi bi-tag" },
        { label: "Omi Store", slug: "omi-store", icon: "bi bi-shop" },
        { label: "Combo", slug: "combo", icon: "bi bi-box-seam" },
        { label: "Aesthetic Treatment", slug: "aesthetic-treatment", icon: "bi bi-magic" },
        { label: "Travel Kit", slug: "travel-kit", icon: "bi bi-bag-check" },
        { label: "Budget Friendly", slug: "budget-friendly", icon: "bi bi-piggy-bank" },
        { label: "Today's Deals", slug: "todays-deals", icon: "bi bi-lightning-charge" },
      ],
    };
  },
  computed: {
    // Duplicate the list so the slider can loop seamlessly in both directions.
    loopCategories() {
      return [...this.categories, ...this.categories];
    },
  },
  mounted() {
    this.$nextTick(this.measure);
    window.addEventListener("resize", this.measure);
    this.rafId = requestAnimationFrame(this.step);
  },
  beforeUnmount() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener("resize", this.measure);
  },
  methods: {
    measure() {
      const track = this.$refs.track;
      // One set is half of the duplicated track's total width.
      if (track) this.setWidth = track.scrollWidth / 2;
    },
    step() {
      const vp = this.$refs.viewport;
      if (vp && !this.hovering && !this.pointerActive) {
        vp.scrollLeft += this.speed;
        this.wrap();
      }
      this.rafId = requestAnimationFrame(this.step);
    },
    wrap() {
      const vp = this.$refs.viewport;
      if (!vp || !this.setWidth) return;
      if (vp.scrollLeft >= this.setWidth) {
        vp.scrollLeft -= this.setWidth;
      } else if (vp.scrollLeft <= 0) {
        vp.scrollLeft += this.setWidth;
      }
    },
    onPointerDown(e) {
      this.pointerActive = true;
      this.dragMoved = false;
      this.startX = e.clientX;
      this.startScroll = this.$refs.viewport.scrollLeft;
      // Only mouse gets JS drag; touch uses native scrolling.
      if (e.pointerType === "mouse") {
        this.isDragging = true;
      }
    },
    onPointerMove(e) {
      if (!this.isDragging) return;
      const delta = e.clientX - this.startX;
      if (Math.abs(delta) > 3) this.dragMoved = true;
      this.$refs.viewport.scrollLeft = this.startScroll - delta;
      this.wrap();
    },
    onPointerUp() {
      this.isDragging = false;
      this.pointerActive = false;
    },
    onItemClick(e) {
      // Prevent navigation if the pointer was dragging (not a real click).
      if (this.dragMoved) {
        e.preventDefault();
        this.dragMoved = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.featured-categories {
  padding: 40px 0;

  .section-title {
    font-weight: 700;
    font-size: 2rem;
    color: #1e1e2d;
    margin-bottom: 2rem;
  }
}

.marquee-viewport {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  cursor: grab;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &.dragging {
    cursor: grabbing;
    user-select: none;
  }
}

.marquee-track {
  display: flex;
  width: max-content;
}

.category-item {
  flex: 0 0 auto;
  width: 150px;
  margin: 0 12px;
  text-decoration: none;
  text-align: center;
  -webkit-user-drag: none;
}

.category-frame {
  width: 150px;
  height: 150px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  transition: transform 0.25s ease;

  .category-icon {
    font-size: 44px;
    color: #fff;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}

.category-item:hover .category-frame {
  transform: translateY(-4px);
}

.category-label {
  margin-top: 14px;
  font-weight: 600;
  font-size: 1rem;
  color: #1e1e2d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
